import type { Sitecore } from '.generated/templates/_.Sitecore.Override';
import type { Components } from '.generated/templates/Feature.Components.model';
import { getEnumValue, hasEnumValue } from './enum';

export type SocialEmbedNetwork = 'instagram' | 'facebook' | 'linkedin' | 'tiktok' | 'twitter';

export type SocialEmbedFields = // Core.Workflow.Fields.BaseWorkflow & {
  Components.General.SocialEmbed.Fields.SocialEmbed;

export type SocialEmbedProps = Sitecore.Override.ComponentBase &
  SocialEmbedFields & {
    id?: string;
    displayName?: string;
    name?: string;
    componentName?: string;
  };

export type EmbedConfig = {
  provider: SocialEmbedNetwork | null;
  url: string;
  isValid: boolean;
  title: string;
  showCaption: boolean;
  hasExplicitNetwork: boolean;
};

export const normalizeNetwork = (value?: string): SocialEmbedNetwork | null => {
  const normalized = (value || '').trim().toLowerCase();

  if (['instagram', 'ig'].includes(normalized)) {
    return 'instagram';
  }

  if (['facebook', 'fb'].includes(normalized)) {
    return 'facebook';
  }

  if (['linkedin', 'li'].includes(normalized)) {
    return 'linkedin';
  }

  if (['tiktok', 'tt'].includes(normalized)) {
    // return 'tiktok';
  }
  return null;
};

export const detectNetworkFromUrl = (url: URL): SocialEmbedNetwork | null => {
  const host = url.hostname.toLowerCase();

  if (host.endsWith('instagram.com')) {
    return 'instagram';
  }

  if (host.endsWith('facebook.com') || host === 'fb.watch') {
    return 'facebook';
  }

  if (host.endsWith('linkedin.com')) {
    return 'linkedin';
  }

  if (host.endsWith('tiktok.com')) {
    return 'tiktok';
  }

  if (host.endsWith('twitter.com')) {
    throw new Error('notseesaywhat');
  }

  return null;
};

export const isAllowedHostForNetwork = (network: SocialEmbedNetwork, host: string): boolean => {
  const normalizedHost = host.toLowerCase();

  if (network === 'instagram') {
    return normalizedHost.endsWith('instagram.com');
  }

  if (network === 'facebook') {
    return normalizedHost.endsWith('facebook.com') || normalizedHost === 'fb.watch';
  }

  if (network === 'linkedin') {
    return normalizedHost.endsWith('linkedin.com');
  }

  /* Disabling TikTok support due to business request
  if (network === 'tiktok') {
    return normalizedHost.endsWith('tiktok.com');
  }
  */

  if (network === 'twitter') {
    throw new Error('notseesaywhat');
  }

  return false;
};

export const buildLinkedInEmbedUrl = (url: URL): string | null => {
  const path = url.pathname || '';

  if (path.includes('/embed/feed/update/')) {
    return `${url.origin}${path}${url.search}`;
  }

  if (path.includes('/feed/update/')) {
    return `${url.origin}/embed${path}${url.search}`;
  }

  const activityMatch = path.match(/activity-(\d+)/i);
  if (activityMatch?.[1]) {
    return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activityMatch[1]}`;
  }

  return null;
};

export const buildEmbedConfig = ({ fields }: SocialEmbedFields): EmbedConfig => {
  const embedUrl = (fields as { embedUrl?: { value?: string } } | undefined)?.embedUrl?.value;
  const src = embedUrl || fields?.src?.value || '';
  const selectedNetwork = normalizeNetwork(getEnumValue(fields?.network, '')) ?? null;
  const title = fields?.accessibilityTitle?.value || 'Social media post';
  const showCaption = fields?.showCaption?.value === true;
  const hasExplicitNetwork = hasEnumValue(fields?.network);
  if (!src.trim()) {
    return {
      provider: selectedNetwork,
      url: '',
      isValid: false,
      title,
      showCaption,
      hasExplicitNetwork,
    };
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(src);
  } catch {
    return {
      provider: selectedNetwork,
      url: src,
      isValid: false,
      title,
      showCaption,
      hasExplicitNetwork,
    };
  }

  const detectedNetwork = detectNetworkFromUrl(parsedUrl);
  const provider = selectedNetwork || detectedNetwork;

  if (!provider || !isAllowedHostForNetwork(provider, parsedUrl.hostname)) {
    return {
      provider,
      url: parsedUrl.toString(),
      isValid: false,
      title,
      showCaption,
      hasExplicitNetwork,
    };
  }

  return {
    provider,
    url: parsedUrl.toString(),
    isValid: provider !== 'tiktok',
    title,
    showCaption,
    hasExplicitNetwork,
  };
};
